import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { captureException } from '@sentry/browser';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';

import {
  FitnessApplicationActivityType,
  FitnessApplicationSettingsInput,
  MeasurementSystem,
  PageTotalsDisplayOptions,
} from '../../../../types/__generated__';
import useUpdateFitnessSettings from '../../../hooks/fitness/useUpdateFitnessSettings';
import { useApp } from '../../../hooks/useApp';
import Button from '../../Button/Button';
import { useFitnessManagerContext } from '../../FitnessManager/FitnessManagerContext';
import { FormikChoiceGroup as ChoiceGroup } from '../ChoiceGroup';
import { Fieldset } from '../Fieldset';
import { FormikSwitch as Switch } from '../Switch';

export interface FitnessSettingsFormValues {
  subscribedActivities: FitnessApplicationActivityType[];
  measurementSystem: MeasurementSystem;
  showDistance: boolean;
  showDuration: boolean;
  showElevation: boolean;
  showPhotos: boolean;
  showMap: boolean;
  showTotaliser: boolean;
}

interface FitnessSettingsFormProps {
  children: ReactNode;
}

export const FitnessSettingsForm = ({ children }: FitnessSettingsFormProps) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { hideToast } = useApp();
  const { onSubmit } = useFormik();
  const {
    status,
    settings,
    fitnessApplicationName,
    modalManager,
  } = useFitnessManagerContext();

  useEffect(() => {
    hideToast();
  }, [hideToast]);

  return (
    <div>
      <div className="jg-display-flex jg-flex-aic jg-flex-column jg-flex-jcc jg-space-pbms">
        <div className="jg-h1 jg-space-mbmd">{t('settings.fitness.title')}</div>
        <div className="jg-text--large-regular jg-space-mbml">
          {t('settings.fitness.desc-part-1')}
        </div>
        <div className="jg-text--large-regular">
          {t('settings.fitness.desc-part-2')}
        </div>
      </div>

      <Formik onSubmit={onSubmit} initialValues={settings}>
        {({
          isValid,
          isSubmitting,
        }: FormikProps<FitnessSettingsFormValues>) => {
          return (
            <Form>
              {children}
              <Button disabled={isSubmitting || !isValid} type="submit">
                {isSubmitting ? (
                  <Loader size="small" colour="white" />
                ) : (
                  t('settings.fitness.save')
                )}
              </Button>
            </Form>
          );
        }}
      </Formik>
      {!status.isPaused && (
        <Button
          className="jg-btn--ghost jg-space-mtms"
          onClick={() => modalManager.actions.onOpenModal('confirmation')}
          type="button"
          trackingData={{
            subtype: 'button',
            event: 'link click',
            page_section: fitnessApplicationName,
          }}
        >
          {t(`settings.fitness.stopSharing.${fitnessApplicationName}`)}
        </Button>
      )}
    </div>
  );
};

const useFormik = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();
  const {
    status,
    page,
    fitnessApplicationName,
    modalManager,
  } = useFitnessManagerContext();

  const [updateFitnessSettings] = useUpdateFitnessSettings();

  const handleSubmit = async (
    values: FitnessSettingsFormValues,
    { setSubmitting }: FormikHelpers<FitnessSettingsFormValues>,
  ) => {
    if (
      !values.subscribedActivities?.length &&
      values.subscribedActivities?.length
    ) {
      modalManager.actions.onOpenModal('confirmation');
      return;
    }

    const input: FitnessApplicationSettingsInput = {
      pageId: page.id,
      name: fitnessApplicationName,
      ...values,
    };

    setSubmitting(true);
    try {
      await updateFitnessSettings(input);

      const message = (() => {
        if (
          status.isConnected &&
          localStorage.getItem(`${fitnessApplicationName}.initialFormSave`)
        ) {
          return t(`${fitnessApplicationName}.connection.success`);
        }
        return t('settings.fitness.update.success');
      })();

      showToast({
        message,
        type: 'success',
        dismissAfter: 5000,
      });
    } catch (error) {
      showToast({
        message: t('settings.fitness.update.warning'),
        type: 'warning',
        dismissAfter: 10000,
      });
      captureException(error);
    }
    localStorage.removeItem(`${fitnessApplicationName}.initialFormSave`);
    setSubmitting(false);
    modalManager.actions.onCloseModal();
  };

  return {
    onSubmit: handleSubmit,
  };
};

interface ActivityTypesProps {
  activityTypes?: FitnessApplicationActivityType[];
}

export const ActivityTypes = ({
  activityTypes = [
    FitnessApplicationActivityType.Run,
    FitnessApplicationActivityType.Ride,
    FitnessApplicationActivityType.Walk,
    FitnessApplicationActivityType.Swim,
    FitnessApplicationActivityType.Wheelchair,
  ],
}: ActivityTypesProps) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { status } = useFitnessManagerContext();

  return (
    <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptml jg-space-pbms">
      <ChoiceGroup
        label={t('settings.fitness.activityTypes')}
        error={status.isPaused ? t('settings.fitness.errorPaused') : undefined}
        name="subscribedActivities"
        multiple={true}
        choices={activityTypes.map(activityType => ({
          label: t(`settings.fitness.${activityType.toLowerCase()}`),
          value: activityType,
        }))}
      />
    </div>
  );
};

export const UnitsOfMeasure = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptsm jg-space-pbsm">
      <ChoiceGroup
        label={t('settings.fitness.measurementSystem')}
        name="measurementSystem"
        choices={[
          {
            label: t('settings.fitness.imperial'),
            value: MeasurementSystem.Imperial,
          },
          {
            label: t('settings.fitness.metric'),
            value: MeasurementSystem.Metric,
          },
        ]}
      />
    </div>
  );
};

interface DisplayPreference {
  children: ReactNode;
}

export const DisplayPreferences = ({ children }: DisplayPreference) => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptsm jg-space-pbsm">
      <Fieldset legend={t('settings.fitness.displayPreferences')}>
        {children}
      </Fieldset>
    </div>
  );
};

export const ShowDistance = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <Switch
      name="showDistance"
      label={t('settings.fitness.showDistance')}
      hint={t('settings.fitness.showDistance-description')}
    />
  );
};

export const ShowDuration = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <Switch
      name="showDuration"
      label={t('settings.fitness.showDuration')}
      hint={t('settings.fitness.showDuration-description')}
    />
  );
};

export const ShowElevation = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <Switch
      name="showElevation"
      label={t('settings.fitness.showElevation')}
      hint={t('settings.fitness.showElevation-description')}
    />
  );
};

export const ShowPhotos = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <Switch
      name="showPhotos"
      label={t('settings.fitness.showPhotos')}
      hint={t('settings.fitness.showPhotos-description')}
    />
  );
};

export const ShowMap = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <Switch
      name="showMap"
      label={t('settings.fitness.showMap')}
      hint={t('settings.fitness.showActivityMap')}
    />
  );
};

export const ShowTotaliser = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { displayOptions } = useFitnessManagerContext();

  if (displayOptions === PageTotalsDisplayOptions.DoNotDisplay) return null;

  return (
    <Switch
      name="showTotaliser"
      label={t('settings.fitness.showTotaliser')}
      hint={t('settings.fitness.showTotaliser.description')}
    />
  );
};

export default FitnessSettingsForm;
FitnessSettingsForm.ActivityTypes = ActivityTypes;
FitnessSettingsForm.UnitsOfMeasurement = UnitsOfMeasure;
FitnessSettingsForm.DisplayPreferences = DisplayPreferences;
DisplayPreferences.ShowDistance = ShowDistance;
DisplayPreferences.ShowDuration = ShowDuration;
DisplayPreferences.ShowElevation = ShowElevation;
DisplayPreferences.ShowPhotos = ShowPhotos;
DisplayPreferences.ShowMap = ShowMap;
DisplayPreferences.ShowTotaliser = ShowTotaliser;
